import { EVENT_BACKGROUNDS, type EventBackground } from '@/lib/eventPalette'
import { deriveExternalPlatform } from '@/lib/externalPlatform'
import type { Event } from '@/types'

export type EventFormPurchasableItem = {
  id: string
  name: string
  description: string | null
  price_dollars: string | null
  inventory_limit: number | null
  max_per_buyer: number | null
  active: boolean
  kind: PurchasableItemKind
  ad_format: 'print' | 'digital' | null
  tickets_per_purchase: number
  sold: boolean
}

export type PurchasableItemKind = 'ticket' | 'sponsorship' | 'ad'

export type PurchasableItemRowState = {
  client_id: string
  id: string
  name: string
  description: string
  price_dollars: string
  inventory_limit: string
  max_per_buyer: string
  active: boolean
  kind: PurchasableItemKind
  ad_format: 'print' | 'digital'
  tickets_per_purchase: string
  sold: boolean
  destroyed: boolean
}

export type EventFormEventData = {
  page_kind: 'event' | 'fundraiser'
  title: string
  description: string
  date: string
  start_time: string
  end_time: string
  venue_name: string
  street_address: string
  city: string
  state: string
  zip: string
  dress_code: string
  starting_ticket_price: string
  ticket_link: string
  purchase_button_text: string
  payment_mode: 'native' | 'external'
  auction_url: string
  donation_url: string
  raffle_url: string
  cover_image: string | null
  remove_cover_image: boolean
  hashtags_input: string
  primary_cause: string
  causes: string[]
  countdown_timer: boolean
  auction_items: boolean
  gift_items: boolean
  donations_enabled: boolean
  show_donation_progress: boolean
  donation_goal_dollars: string
  status: 'draft' | 'published'
  visibility: 'public' | 'private'
  template_style: 'classic' | 'gallery'
  background_color: EventBackground
  hosts: Event['hosts']
  honorees: Event['honorees']
  entertainment: Event['entertainment']
  sponsors: Event['sponsors']
  committee_members: Event['committee_members']
}

export type FieldError = (field: string, tierId?: string) => string | undefined

export const DRESS_CODE_LABELS: Record<string, string> = {
  black_tie: 'Black Tie',
  black_tie_optional: 'Black Tie Optional',
  cocktail_attire: 'Cocktail Attire',
  business_casual: 'Business Casual',
  formal: 'Formal',
  corporate: 'Corporate',
  corporate_festive: 'Corporate Festive',
  festive: 'Festive',
  festive_black_tie: 'Festive Black Tie',
  sport_casual: 'Sport Casual',
  white_tie: 'White Tie',
  casual: 'Casual',
  beach_formal: 'Beach Formal',
  garden_attire: 'Garden Attire',
}

export { EVENT_BACKGROUNDS }
export type { EventBackground }

export function formatTimeForInput(timeStr: string | null): string {
  if (!timeStr) return ''
  const date = new Date(timeStr)
  const hours = date.getUTCHours().toString().padStart(2, '0')
  const minutes = date.getUTCMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

export function formatDateForInput(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  return dateStr.split('T')[0]
}

export function newPurchasableItemRow(
  kind: PurchasableItemKind,
  seed?: EventFormPurchasableItem | null,
): PurchasableItemRowState {
  return {
    client_id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
    id: seed?.id ?? '',
    name: seed?.name ?? '',
    description: seed?.description ?? '',
    price_dollars: seed?.price_dollars ?? '',
    inventory_limit: seed?.inventory_limit != null ? String(seed.inventory_limit) : '',
    max_per_buyer: seed?.max_per_buyer != null ? String(seed.max_per_buyer) : '',
    active: seed?.active ?? true,
    kind: seed?.kind ?? kind,
    ad_format: seed?.ad_format ?? 'print',
    tickets_per_purchase: seed?.tickets_per_purchase != null ? String(seed.tickets_per_purchase) : '1',
    sold: seed?.sold ?? false,
    destroyed: false,
  }
}

export function buildPurchasableItemsAttributes(rows: PurchasableItemRowState[]) {
  return rows
    .map((row) => {
      if (row.destroyed) return row.id ? { id: row.id, _destroy: true } : null
      const hasInput = row.name.trim() !== '' || row.price_dollars.trim() !== ''
      if (!row.id && !hasInput) return null

      const attrs: Record<string, unknown> = {
        name: row.name,
        description: row.description || null,
        price_dollars: row.price_dollars,
        inventory_limit: row.inventory_limit === '' ? null : row.inventory_limit,
        max_per_buyer: row.max_per_buyer === '' ? null : row.max_per_buyer,
        active: row.active,
        kind: row.kind,
        ad_format: row.kind === 'ad' ? row.ad_format : null,
        tickets_per_purchase: row.kind === 'ad'
          ? 1
          : (row.tickets_per_purchase === '' ? 1 : row.tickets_per_purchase),
      }
      if (row.id) attrs.id = row.id
      return attrs
    })
    .filter((row): row is Record<string, unknown> => row !== null)
}

export function transformForPreview(formData: EventFormEventData, coverImagePreview: string | null): Event {
  const hashtags = formData.hashtags_input
    .split(',')
    .map(t => t.trim())
    .filter(Boolean)

  const buildTime = (val: string): string | null => (val ? `2000-01-01T${val}:00Z` : null)

  return {
    id: '',
    slug: '',
    organization_id: '',
    title: formData.title || '',
    description: formData.description || null,
    date: formData.date || null,
    page_kind: formData.page_kind || 'event',
    start_time: buildTime(formData.start_time),
    end_time: buildTime(formData.end_time),
    venue_name: formData.venue_name || null,
    street_address: formData.street_address || null,
    city: formData.city || null,
    state: formData.state || null,
    zip: formData.zip || null,
    dress_code: formData.dress_code || null,
    starting_ticket_price: formData.starting_ticket_price || null,
    ticket_link: formData.ticket_link || null,
    purchase_button_text: formData.purchase_button_text || null,
    payment_mode: formData.payment_mode,
    external_platform: deriveExternalPlatform(formData.ticket_link || ''),
    auction_url: formData.auction_url || null,
    donation_url: formData.donation_url || null,
    raffle_url: formData.raffle_url || null,
    cover_image_url: coverImagePreview,
    hashtags,
    countdown_timer: formData.countdown_timer,
    auction_items: formData.auction_items,
    gift_items: formData.gift_items,
    primary_cause: formData.primary_cause || null,
    causes: formData.causes,
    status: formData.status,
    visibility: formData.visibility,
    template_style: formData.template_style,
    background_color: formData.background_color,
    hosts: formData.hosts,
    honorees: formData.honorees,
    entertainment: formData.entertainment,
    sponsors: formData.sponsors,
    committee_members: formData.committee_members,
    donations_enabled: formData.donations_enabled,
    show_donation_progress: formData.show_donation_progress,
    donation_goal_cents: formData.donation_goal_dollars
      ? Math.round(parseFloat(formData.donation_goal_dollars) * 100)
      : undefined,
  }
}
